<div class="card border-0 shadow-sm security-settings-card">
  <div class="card-header bg-white">
    <h5 class="mb-1 fw-semibold">
      <i class="bi bi-shield-lock-fill text-primary me-2"></i>
      Security Settings
    </h5>

    <small class="text-muted"> Manage your practice's MFA policy and account password. </small>
  </div>

  <div class="card-body">
    <div class="alert alert-info d-flex align-items-start security-note">
      <i class="bi bi-info-circle-fill me-2 mt-1"></i>
      <div>
        Security controls should feel effortless to manage, but strict enough to keep the practice
        account safe.
      </div>
    </div>

    <div class="card border-0 bg-light mb-4 security-form-card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 class="mb-1 fw-semibold">
              <i class="bi bi-people-fill me-1 text-primary"></i>Team MFA Policy
            </h6>
            @if (isManager) {
              <small class="text-muted"
                >Enforce two-factor authentication for all staff and doctors in this
                practice.</small
              >
            } @else {
              <small class="text-muted"
                >MFA policy set by your practice manager. Contact your manager to change
                this.</small
              >
            }
          </div>

          @if (isTeamMfaLoading) {
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          } @else if (isManager) {
            <div class="d-flex align-items-center gap-2">
              <div class="form-check form-switch mb-0">
                <input
                  class="form-check-input fs-5"
                  type="checkbox"
                  id="teamMfaEnabled"
                  [(ngModel)]="teamMfaEnabled"
                />
              </div>
              <button
                class="btn btn-sm btn-primary"
                [disabled]="isSavingTeamMfa"
                (click)="saveTeamMfaSetting()"
              >
                <i class="bi bi-floppy-fill me-1"></i>{{ isSavingTeamMfa ? 'Saving...' : 'Apply' }}
              </button>
            </div>
          } @else {
            <span
              class="badge rounded-pill fs-6"
              [class.bg-success]="teamMfaEnabled"
              [class.bg-secondary]="!teamMfaEnabled"
            >
              <i
                class="bi me-1"
                [class.bi-shield-lock-fill]="teamMfaEnabled"
                [class.bi-shield]="!teamMfaEnabled"
              ></i>
              {{ teamMfaEnabled ? 'Enforced' : 'Not enforced' }}
            </span>
          }
        </div>

        @if (!isManager && teamMfaEnabled) {
          <div class="alert alert-warning d-flex align-items-center gap-2 mt-3 mb-0 py-2">
            <i class="bi bi-exclamation-triangle-fill"></i>
            Your manager has enforced MFA for this practice. Two-factor authentication is required.
          </div>
        }
      </div>
    </div>

    <div class="card border-0 bg-light mb-4 security-form-card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 class="mb-1 fw-semibold">
              <i class="bi bi-clock-history me-1 text-primary"></i>Password Rotation Policy
            </h6>
            @if (isManager) {
              <small class="text-muted"
                >Require staff and doctors to update their password after a specified number of
                days.</small
              >
            } @else {
              <small class="text-muted"
                >Password rotation policy set by your practice manager. Contact your manager to
                change this.</small
              >
            }
          </div>

          @if (isTeamMfaLoading) {
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          } @else if (isManager) {
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <div class="input-group" style="width: fit-content">
                <input
                  type="number"
                  class="form-control"
                  style="width: 120px"
                  [(ngModel)]="passwordRotationDays"
                  placeholder="Days (0 = no rotation)"
                  min="0"
                  max="90"
                />
                <span class="input-group-text">days</span>
              </div>
              <button
                class="btn btn-sm btn-primary"
                [disabled]="isSavingTeamMfa || !isPasswordRotationValid()"
                (click)="saveTeamMfaSetting()"
              >
                <i class="bi bi-floppy-fill me-1"></i>{{ isSavingTeamMfa ? 'Saving...' : 'Apply' }}
              </button>
            </div>
            @if ((passwordRotationDays ?? 0) > 0 && (passwordRotationDays ?? 0) < 30) {
              <small class="text-danger d-block mt-2">
                <i class="bi bi-exclamation-circle-fill me-1"></i>Minimum allowed is 30 days (or 0
                to disable)
              </small>
            }
            @if ((passwordRotationDays ?? 0) > 90) {
              <small class="text-danger d-block mt-2">
                <i class="bi bi-exclamation-circle-fill me-1"></i>Maximum allowed is 90 days
              </small>
            }
          } @else {
            <span
              class="badge rounded-pill fs-6"
              [class.bg-warning]="passwordRotationDays"
              [class.bg-secondary]="!passwordRotationDays"
            >
              <i
                class="bi me-1"
                [class.bi-clock-history]="passwordRotationDays"
                [class.bi-x-circle]="!passwordRotationDays"
              ></i>
              {{ passwordRotationDays ? passwordRotationDays + ' days' : 'Not enforced' }}
            </span>
          }
        </div>

        @if (!isManager && passwordRotationDays) {
          <div class="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0 py-2">
            <i class="bi bi-info-circle-fill"></i>
            Your manager requires password changes every {{ passwordRotationDays }} days.
          </div>
        }
      </div>
    </div>

    <div class="card border-0 bg-light security-form-card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h6 class="mb-1 fw-semibold">Password</h6>
            <small class="text-muted">Update your account password regularly.</small>
          </div>

          <button
            class="btn btn-outline-primary"
            (click)="showPasswordSection = !showPasswordSection"
          >
            <i class="bi bi-key-fill me-2"></i>

            {{ showPasswordSection ? 'Cancel' : 'Change Password' }}
          </button>
        </div>

        @if (showPasswordSection) {
          <div class="row g-3 mt-3">
            <div class="col-md-4">
              <label class="form-label fw-semibold"> Current Password </label>

              <input
                type="password"
                class="form-control"
                [(ngModel)]="passwordRequest.currentPassword"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold"> New Password </label>

              <input
                type="password"
                class="form-control"
                [(ngModel)]="passwordRequest.newPassword"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold"> Confirm Password </label>

              <input
                type="password"
                class="form-control"
                [(ngModel)]="passwordRequest.confirmPassword"
              />
            </div>
          </div>

          <div class="text-end mt-3">
            <button
              class="btn btn-primary"
              [disabled]="isChangingPassword"
              (click)="changePassword()"
            >
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ isChangingPassword ? 'Updating...' : 'Update Password' }}
            </button>
          </div>
        }
      </div>
    </div>
  </div>
</div>
